import { Stack, Typography } from '@mui/material';
import { SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { useSchoolProperties } from '@schooly/hooks/use-school-properties';
import { TagSelect, TagSelectProperty } from '@schooly/style';
import { FC, PropsWithChildren, useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { SelectContentSkeleton } from '../SelectContentSkeleton';
import { SelectSearchInput } from '../SelectSearchInput';
import { filterPropertiesByQuery, toggleMultipleValueArrayProperty } from '../utils';
import { DottedDivider } from './DottedDivider.styled';
import { PropertyTypeTagSelect, PropertyTypeTagSelectProps } from './PropertyTypeTagSelect';

type PropertyTypeExpandedSelectProps = PropsWithChildren<{
  propertyType: SchoolPropertyType;
  userRole: SchoolUserRole;
  schoolId: string;
  onClear: () => void;
  onClose: () => void;
  showArchived?: boolean;
  applyImmediate?: boolean;
}> &
  (
    | { onApply: (v: string[]) => void; selectedIds: string[] }
    | {
        onApply: (v: string[] | null) => void;
        noneOptionLabel: string;
        selectedIds: string[] | null;
      }
  );

export const PropertyTypeExpandedSelect: FC<PropertyTypeExpandedSelectProps> = (props) => {
  const {
    propertyType,
    selectedIds: initialSelectedIds,
    onClear,
    onClose,
    showArchived,
    schoolId,
    userRole,
    applyImmediate,
  } = props;

  const [selectedIds, setSelectedIds] = useState<string[] | null>(initialSelectedIds);
  const inputRef = useRef<HTMLInputElement>(null);

  const isNoneOptionSelected = selectedIds === null;

  const hasNoneOption = 'noneOptionLabel' in props;

  const applyIds = useCallback(
    (ids: string[] | null) => {
      if (hasNoneOption) {
        props.onApply(ids);
      } else {
        props.onApply(ids ?? []);
      }
    },
    // No need for exhaustive deps because we use props.onApply directly
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onApply, hasNoneOption],
  );

  const toggleIds = useCallback(
    (v: string[] | null) => {
      setSelectedIds((ids) => {
        const newIds = v === null ? null : toggleMultipleValueArrayProperty(ids ?? [], v);
        if (applyImmediate) {
          applyIds(newIds);
        }
        return newIds;
      });
    },
    [applyImmediate, applyIds],
  );

  const handleApply = useCallback(() => {
    applyIds(selectedIds);
    onClose();
  }, [applyIds, selectedIds, onClose]);

  const { activePropertiesMap, archivedPropertiesMap, isLoading } = useSchoolProperties({
    schoolId,
    userType: userRole,
    showReEnrollmentProperties: true,
  });

  const [query, setQuery] = useState('');

  const renderContent = useCallback(() => {
    if (isLoading) return <SelectContentSkeleton />;

    const activeProperties = filterPropertiesByQuery(query, activePropertiesMap[propertyType]);
    const archivedProperties = filterPropertiesByQuery(query, archivedPropertiesMap[propertyType]);

    const showNoneOption =
      'noneOptionLabel' in props &&
      props.noneOptionLabel?.toLowerCase().includes(query.toLowerCase());

    if (!activeProperties.length && !archivedProperties.length && !showNoneOption)
      return (
        <Typography p={1}>
          <FormattedMessage id="input-NoOptionsFound" />
        </Typography>
      );

    return (
      <Stack m={1} gap={2}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {activeProperties.map((property) => (
            <TagSelectProperty
              key={property.id}
              userRole={userRole}
              property={property}
              variant={selectedIds?.includes(property.id) ? 'filled' : undefined}
              onClick={() => {
                toggleIds([property.id]);
                setQuery('');
              }}
            />
          ))}
        </Stack>
        {(showArchived || Boolean(archivedProperties.length || showNoneOption)) && (
          <>
            <DottedDivider mx={-1} />
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {showNoneOption && (
                <TagSelect
                  label={props.noneOptionLabel}
                  variant={isNoneOptionSelected ? 'filled' : undefined}
                  onClick={() => {
                    toggleIds(null);
                    setQuery('');
                  }}
                />
              )}
              {archivedProperties.map((property) => (
                <TagSelectProperty
                  key={property.id}
                  userRole={userRole}
                  property={property}
                  variant={selectedIds?.includes(property.id) ? 'filled' : undefined}
                  onClick={() => {
                    toggleIds([property.id]);
                    setQuery('');
                  }}
                />
              ))}
            </Stack>
          </>
        )}
        {!applyImmediate && <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />}
      </Stack>
    );
  }, [
    isLoading,
    query,
    activePropertiesMap,
    propertyType,
    archivedPropertiesMap,
    props,
    showArchived,
    isNoneOptionSelected,
    handleApply,
    onClear,
    userRole,
    selectedIds,
    toggleIds,
    applyImmediate,
  ]);

  return (
    <ExpandedSelect
      hasSelectedValue={(selectedIds && selectedIds?.length > 0) || selectedIds === null}
      onClose={onClose}
      onClear={onClear}
      onClickInputArea={() => inputRef.current?.focus()}
      renderContent={renderContent}
    >
      {isNoneOptionSelected && 'noneOptionLabel' in props ? (
        <TagSelect
          label={props.noneOptionLabel}
          sx={{ maxWidth: 200 }}
          onDelete={() => setSelectedIds([])}
          size="small"
        />
      ) : (
        renderPropertyTypeTags({
          ids: selectedIds ?? [],
          onDelete: (id) => toggleIds([id]),
          tagProps: {
            schoolId,
            userRole,
            size: 'small',
          },
        })
      )}
      <SelectSearchInput ref={inputRef} autoFocus value={query} onChangeText={setQuery} />
    </ExpandedSelect>
  );
};

export type RenderPropertyTypeTagsParams = {
  ids: string[];
  onDelete?: (v: string) => void;
  onClick?: (v: string) => void;
  tagProps:
    | Omit<PropertyTypeTagSelectProps, 'id'>
    | ((v: string) => Omit<PropertyTypeTagSelectProps, 'id'>);
};

export const renderPropertyTypeTags = ({
  ids,
  onDelete,
  onClick,
  tagProps,
}: RenderPropertyTypeTagsParams) =>
  ids.map((v) => (
    <PropertyTypeTagSelect
      key={v}
      id={v}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
    />
  ));
